// Pure parsers for the bulk registration surfaces (paste / CSV / manual table).
// Extracted from RegistrationTab.jsx so they're unit-testable under `node --test`.
//
// STEP 3 — per-player handicap capture. Each parser can now emit a
// `playerHandicaps` array (parallel to `players`) built with buildHandicapEntry,
// stamped source:'self' (bulk import is inherently unverified). The flight-lock
// snapshot (data.js) reads playerHandicaps[i].handicapIndex to compute per-team
// net playingHandicaps. The old single team-level `handicap` field still parses
// unchanged for backward compatibility.
import { buildHandicapEntry } from './handicapEntry.js'

// Member flag from a CSV "Member" column: Y / Yes / true / 1 / Member → member
// team; blank / N / anything else → not a member.
export function parseMemberFlag(v) {
  const s = String(v ?? '').trim().toLowerCase()
  return s === 'y' || s === 'yes' || s === 'true' || s === '1' || s === 'member' || s === 'm'
}

// Build a playerHandicaps array parallel to a team's players from per-player
// index values (strings or numbers). Returns null when NO player carries an
// index — keeps backward-compat rows lean and avoids writing empty arrays.
// Course params (ctx) fill courseHandicap/playingHandicap; both calc helpers are
// NaN-safe, so a missing/unset course just yields 0 there — the authoritative
// net recompute happens at flight-lock from handicapIndex, so that's harmless.
export function buildPlayerHandicaps(indexes, ctx = {}) {
  const hasAny = (indexes || []).some(
    (v) => v != null && v !== '' && Number.isFinite(Number(v)),
  )
  if (!hasAny) return null
  return indexes.map((v) => buildHandicapEntry({
    index: v == null || v === '' ? null : Number(v),
    source: 'self',
    slopeRating: ctx.slopeRating,
    courseRating: ctx.courseRating,
    coursePar: ctx.coursePar,
    allowance: ctx.allowance,
  }))
}

// Pull a trailing "(index)" off a player token → [cleanName, indexString|null].
// e.g. "Mark Ratliff (8.3)" → ["Mark Ratliff", "8.3"].
function splitPlayerIndex(token) {
  const m = token.match(/\(\s*(\d+(?:\.\d+)?)\s*\)\s*$/)
  if (m) return [token.slice(0, m.index).trim(), m[1]]
  return [token, null]
}

// Parse free-form team lines. Supports:
// - "A / B — 8"           split players on /, team-level handicap
// - "A, B, 8"             commas
// - "A & B HCP 8"         ampersand + HCP literal
// - "A and B"             "and" separator
// - "A (8.3) / B (12)"    per-player index in parentheses (STEP 3)
export function parsePaste(text, ctx = {}) {
  const out = []
  for (const raw of (text || '').split(/\r?\n/)) {
    const line = raw.trim()
    if (!line) continue
    // pull out a team-level handicap if present
    let hcp = null
    let body = line
    const hcpMatch = line.match(/(?:\bHCP\b|—|–|\b-\b)\s*(\d+(?:\.\d+)?)/i)
    if (hcpMatch) {
      hcp = Number(hcpMatch[1])
      body = line.slice(0, hcpMatch.index).trim().replace(/[—–-]\s*$/, '').trim()
    } else {
      // last comma-number?
      const csvHcp = line.match(/,\s*(\d+(?:\.\d+)?)\s*$/)
      if (csvHcp) {
        hcp = Number(csvHcp[1])
        body = line.slice(0, csvHcp.index).trim()
      }
    }
    // Split into two players. Separator can be a TAB (spreadsheet / Google-Doc
    // paste — this was the real break in the club's export), a slash of any
    // width, an ampersand, or the word "and". The old parser only knew "/" & "and",
    // so a tab-separated "First Last⇥First Last" line fell through and dumped the
    // whole line into one field.
    const TWO_PLAYER_SEP = /(?:\s*[\t/∕⁄／&]\s*|\s+and\s+)/i
    let parts = []
    if (TWO_PLAYER_SEP.test(body)) {
      parts = body.split(TWO_PLAYER_SEP).map((s) => s.trim()).filter(Boolean)
    } else if (body.includes(',')) {
      parts = body.split(',').map((s) => s.trim()).filter(Boolean)
    } else {
      // last-resort: just use the whole thing as a team name with no players
      parts = [body]
    }
    // Pull a trailing (index) off each player token → per-player handicap.
    const perIdx = []
    const players = parts.slice(0, 2).map((p) => {
      const [name, idx] = splitPlayerIndex(p)
      perIdx.push(idx)
      return name
    })
    const teamName = players.length === 2
      ? `${players[0]} / ${players[1]}`
      : players[0]
    const row = { teamName, players, handicap: hcp }
    const ph = buildPlayerHandicaps(perIdx, ctx)
    if (ph) row.playerHandicaps = ph
    out.push(row)
  }
  return out
}

// Minimal CSV split — handles double-quoted fields with embedded commas.
export function splitCsvLine(line) {
  const out = []
  let cur = ''
  let inQ = false
  for (let i = 0; i < line.length; i++) {
    const c = line[i]
    if (inQ) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++ }
      else if (c === '"') { inQ = false }
      else cur += c
    } else {
      if (c === ',') { out.push(cur); cur = '' }
      else if (c === '"') { inQ = true }
      else cur += c
    }
  }
  out.push(cur)
  return out
}

// Parse a registration CSV. Columns are mapped by header when present:
//   Team Name, Player 1, Player 2, Email, Phone, Handicap
// STEP 3 adds optional per-player columns "Handicap 1" / "Handicap 2" (also
// matches "HCP 1", "Index 1", "Player 1 Handicap", …). The old single
// "Handicap" column still parses as a team-level handicap (backward compat).
export function parseCsv(text, ctx = {}) {
  const lines = (text || '').split(/\r?\n/).filter((l) => l.trim().length > 0)
  if (lines.length === 0) return []
  // Header detection
  const header = lines[0].toLowerCase()
  const hasHeader = /team|player|name|handicap|hcp|email|index|member/i.test(header)
  const rows = (hasHeader ? lines.slice(1) : lines).map((l) => splitCsvLine(l))
  // Try to map columns by header
  let idx = { team: 0, p1: 1, p2: 2, email: 3, phone: 4, hcp: 5, p1hcp: -1, p2hcp: -1 }
  if (hasHeader) {
    const cols = splitCsvLine(lines[0]).map((c) => c.toLowerCase().trim())
    const find = (re) => cols.findIndex((c) => re.test(c))
    idx = {
      team: find(/team\s*name|team$/),
      p1:   find(/player\s*1|p1\b/),
      p2:   find(/player\s*2|p2\b/),
      email: find(/email/),
      phone: find(/phone/),
      // team-level handicap: a bare "handicap"/"hcp" column (no digit suffix)
      hcp:   find(/^(team\s*)?(handicap|hcp)$/),
      // per-player handicap index columns
      p1hcp: find(/(handicap|hcp|index)\s*1\b|player\s*1.*(handicap|hcp|index)|p1\s*(handicap|hcp|index)/),
      p2hcp: find(/(handicap|hcp|index)\s*2\b|player\s*2.*(handicap|hcp|index)|p2\s*(handicap|hcp|index)/),
      member: find(/^member$|is\s*member|membership/),
    }
  }
  const out = []
  for (const r of rows) {
    const teamName = idx.team >= 0 ? (r[idx.team] || '').trim() : ''
    const p1 = idx.p1 >= 0 ? (r[idx.p1] || '').trim() : ''
    const p2 = idx.p2 >= 0 ? (r[idx.p2] || '').trim() : ''
    if (!p1 && !p2 && !teamName) continue
    const p1hcp = idx.p1hcp >= 0 ? (r[idx.p1hcp] || '').trim() : ''
    const p2hcp = idx.p2hcp >= 0 ? (r[idx.p2hcp] || '').trim() : ''
    // Keep players and their indexes aligned in lockstep (drop blank slots).
    const pairs = [[p1, p1hcp], [p2, p2hcp]].filter(([nm]) => nm)
    const players = pairs.map(([nm]) => nm)
    const hcpRaw = idx.hcp >= 0 ? (r[idx.hcp] || '').trim() : ''
    const row = {
      teamName: teamName || (players.length === 2 ? `${p1} / ${p2}` : p1 || p2),
      players,
      email: idx.email >= 0 ? (r[idx.email] || '').trim() || null : null,
      phone: idx.phone >= 0 ? (r[idx.phone] || '').trim() || null : null,
      handicap: hcpRaw === '' ? null : (Number.isFinite(Number(hcpRaw)) ? Number(hcpRaw) : null),
    }
    if (idx.p1hcp >= 0 || idx.p2hcp >= 0) {
      const ph = buildPlayerHandicaps(pairs.map(([, h]) => h), ctx)
      if (ph) row.playerHandicaps = ph
    }
    if (idx.member >= 0) row.member = parseMemberFlag(r[idx.member])
    out.push(row)
  }
  return out
}
